import { ActionField, AsyncField, Field, useFormContext } from '@oscar/form';
import { Col, Icon, Input, Row, Select } from '@oscar/theme';
import { DatePickerPopulate, TextField } from 'common/components';
import { FORMAT_DATE } from 'common/constants';
import { TYPE_OF_WORK } from 'common/constants/insulation';
import { useAuth } from 'features/auth';
import { getDateCorrect, resetDate } from 'features/insulation/utils';
import moment from 'moment';
import { StatusInstance } from 'views/pages/create-instrulation/hooks/useStatus';
import { getTypeOfWorkByURL } from 'views/pages/create-instrulation/utils';
import '../style.scss';
import { APIS } from 'common/constants/endpoints';
import { debounce } from 'lodash';

const { TextArea } = Input;
const { Option } = Select;

interface Props {
  statusRequest: StatusInstance;
}

export const InsulationDetail = ({ statusRequest }: Props) => {
  const [formContext] = useFormContext();
  const [auth] = useAuth();
  const metaDataInsulation =
    formContext.selectors?.getDataByKey('metaDataInsulationDetail') || {};

  const disableSiteVisitDate = (current: any) => {
    const startDate = formContext.form?.getFieldValue('startDate');
    const endDate = formContext.form?.getFieldValue('endDate');

    if (startDate) {
      return (
        (current && current.isBefore(moment(), 'day')) ||
        current.isAfter(getDateCorrect(startDate), 'day')
      );
    }
    if (endDate) {
      return (
        (current && current.isAfter(getDateCorrect(endDate))) ||
        current.isBefore(moment(), 'day')
      );
    }
    return current && current.isBefore(moment(), 'day');
  };

  const disableEndDate = (current: any) => {
    const siteVisitDate = formContext.form?.getFieldValue('siteVisitDate');
    const startDate = formContext.form?.getFieldValue('startDate');
    if (startDate && getDateCorrect(startDate).isAfter(moment(), 'day')) {
      return current && current.isBefore(getDateCorrect(startDate), 'day');
    } else if (
      siteVisitDate &&
      getDateCorrect(siteVisitDate).isAfter(moment(), 'day')
    ) {
      return current && current.isBefore(getDateCorrect(siteVisitDate), 'day');
    } else {
      return current && current.isBefore(moment(), 'day');
    }
  };

  const disableStartDate = (current: any) => {
    const siteVisitDate = formContext.form?.getFieldValue('siteVisitDate');
    const endDate = formContext.form?.getFieldValue('endDate');

    if (siteVisitDate || endDate) {
      return (
        (current &&
          (siteVisitDate &&
          getDateCorrect(siteVisitDate).isAfter(moment(), 'day')
            ? current.isBefore(getDateCorrect(siteVisitDate), 'day')
            : current.isBefore(moment(), 'day'))) ||
        current.isAfter(getDateCorrect(endDate), 'day')
      );
    }
    return current && current.isBefore(getDateCorrect(moment()), 'day');
  };

  return (
    <div className="insulation-detail">
      <ActionField
        name="resetDate"
        request={{
          name: 'resetDate',
        }}
      >
        {(actionProps: any) => {
          if (
            statusRequest?.status === 'DRAFT' &&
            auth.checkRole('PENDING_VALIDTION', 'EDITED')
          ) {
            resetDate(actionProps?.field, statusRequest?.state);
          }
          return <></>;
        }}
      </ActionField>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          {statusRequest.state === 'VIEW' ||
          statusRequest.status === 'IN_SERVICE' ? (
            <Field name="service">
              <TextField
                label="Type of Insulation Work"
                mappingData={TYPE_OF_WORK}
              />
            </Field>
          ) : (
            <>
              <p>Type of Insulation Work</p>
              <Input disabled value={getTypeOfWorkByURL(statusRequest.type)} />
            </>
          )}
        </Col>
      </Row>
      <Row gutter={[15, 15]}>
        <Col span={12}>
          {statusRequest.state === 'VIEW' ||
          statusRequest.status === 'IN_SERVICE' ? (
            <Field name="area">
              <TextField label="Area" mappingData={metaDataInsulation?.area} />
            </Field>
          ) : (
            <>
              <p>Area</p>
              <Field name="area">
                <Select placeholder="Select area" className="area" allowClear>
                  {metaDataInsulation?.area?.map?.(
                    (item: any, index: number) => (
                      <Option key={`${item.id}-${index}`} value={item.id}>
                        {item?.label}
                      </Option>
                    ),
                  )}
                </Select>
              </Field>
            </>
          )}
        </Col>
        <Col span={12}>
          {statusRequest.state === 'VIEW' ||
          statusRequest.status === 'IN_SERVICE' ? (
            <Field name="siteVisitDate">
              <TextField
                label="Site Visit Date"
                isDate
                formartDate={FORMAT_DATE.FULL_DATETIME_HOUR}
              />
            </Field>
          ) : (
            <>
              <p>Site Visit Date</p>
              <Field name="siteVisitDate" dependencies={['startDate']}>
                <DatePickerPopulate
                  showTime={{ format: 'HH:mm A', use12Hours: true }}
                  format={FORMAT_DATE.FULL_DATETIME_HOUR}
                  disabledDate={disableSiteVisitDate}
                  inputReadOnly
                />
              </Field>
            </>
          )}
        </Col>
      </Row>
      <Row gutter={[15, 15]}>
        <Col span={12}>
          {statusRequest.state === 'VIEW' ? (
            <Field name="startDate">
              <TextField
                label="Start Date"
                isDate
                formartDate={FORMAT_DATE.SHORT_MONTH_DATE}
              />
            </Field>
          ) : (
            <>
              <p>Start Date</p>
              <Field name="startDate" dependencies={['siteVisitDate']}>
                <DatePickerPopulate
                  disabledDate={disableStartDate}
                  format={FORMAT_DATE.SHORT_MONTH_DATE}
                  inputReadOnly
                />
              </Field>
            </>
          )}
        </Col>
        <Col span={12}>
          {statusRequest.state === 'VIEW' ? (
            <Field name="endDate">
              <TextField
                label="End Date"
                isDate
                formartDate={FORMAT_DATE.SHORT_MONTH_DATE}
              />
            </Field>
          ) : (
            <>
              <p>End Date</p>
              <Field
                name="endDate"
                dependencies={['siteVisitDate', 'startDate']}
              >
                <DatePickerPopulate
                  disabledDate={disableEndDate}
                  format={FORMAT_DATE.SHORT_MONTH_DATE}
                  inputReadOnly
                />
              </Field>
            </>
          )}
        </Col>
      </Row>
      <Row gutter={[15, 15]}>
        <Col span={12}>
          {statusRequest.state === 'VIEW' ? (
            statusRequest.status !== 'DRAFT' && (
              <Field name="serviceProviderId">
                <TextField
                  label="Service Provide"
                  mappingData={metaDataInsulation?.serviceProvider}
                />
              </Field>
            )
          ) : (
            <>
              {auth.checkRole(
                'PENDING_VALIDTION',
                'SUBMITTED',
                statusRequest.type == 'PAINTING' ? 'PAINTING' : 'INSULATION',
              ) &&
                auth.getUserRoles().length === 1 && (
                  <>
                    <p>Service Provider</p>
                    <Field name="serviceProviderId">
                      <Select
                        placeholder="Service Provider"
                        className="serviceProvider"
                        allowClear
                        showSearch
                        suffixIcon={<Icon icon="search" size={18} />}
                        filterOption={(input: any, option: any) =>
                          option.children
                            ?.toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                      >
                        {metaDataInsulation?.serviceProvider?.map?.(
                          (item: any, index: number) => (
                            <Option key={`${item.id}-${index}`} value={item.id}>
                              {item?.label}
                            </Option>
                          ),
                        )}
                      </Select>
                    </Field>
                  </>
                )}
            </>
          )}
        </Col>
        <Col span={12}>
          {statusRequest.state === 'VIEW' ? (
            statusRequest.status !== 'DRAFT' && (
              <Field name="qaqcSupervisor">
                <TextField label="QAQC Supervisor" />
              </Field>
            )
          ) : (
            <>
              {auth.checkRole(
                'PENDING_VALIDTION',
                'SUBMITTED',
                statusRequest.type == 'PAINTING' ? 'PAINTING' : 'INSULATION',
              ) &&
                auth.getUserRoles().length === 1 && (
                  <>
                    <p>QAQC Supervisor </p>
                    <AsyncField
                      name={'fetchInspectorName'}
                      requests={[
                        {
                          name: 'inspectorName',
                          url: APIS.AZURE_AD_USER,
                          method: 'POST',
                          nextParams: (nextProps: any) => {
                            return {
                              limit: 6,
                              name: nextProps?.customData?.textSearch || '',
                              type: -1,
                            };
                          },
                        },
                      ]}
                    >
                      {(inspectorNameProps: any) => {
                        const options =
                          inspectorNameProps?.data?.inspectorName || [];
                        const textSearch =
                          inspectorNameProps?.requests?.[0]?.params?.name || '';

                        return (
                          <>
                            <Field name={['qaqcSupervisorName']}>
                              <Select
                                value={textSearch}
                                showSearch
                                suffixIcon={<Icon icon="search" size={18} />}
                                className="select-search"
                                placeholder="Search Directory"
                                allowClear
                                filterOption={false}
                                onSearch={debounce(value => {
                                  inspectorNameProps.reloadData({
                                    textSearch: value,
                                  });
                                }, 300)}
                              >
                                {options.map?.((item: any) => {
                                  formContext.form?.setFieldsValue({
                                    email: item.email,
                                  });

                                  return (
                                    <Option
                                      value={item.displayName}
                                      key={item.displayName}
                                    >
                                      {item?.displayName}
                                    </Option>
                                  );
                                })}
                              </Select>
                            </Field>
                          </>
                        );
                      }}
                    </AsyncField>
                  </>
                )}
            </>
          )}
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          {statusRequest.state === 'VIEW' ||
          statusRequest.status === 'IN_SERVICE' ? (
            <Field name="workDescription">
              <TextField label="Work Description" />
            </Field>
          ) : (
            <>
              <p>Work Description</p>
              <Field name="workDescription">
                <TextArea
                  placeholder="Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua."
                  autoSize={{ minRows: 6, maxRows: 5 }}
                  className="work-description"
                  maxLength={100}
                />
              </Field>
            </>
          )}
        </Col>
      </Row>

      {/* <Row>
        <Col span={24}>
          <FileUploaded
            name="attachments"
            title="Attachment"
            request={{
              name: 'attachment',
              url: APIS.UPLOADED_FILE,
              method: 'POST',
            }}
          />
        </Col>
      </Row> */}
    </div>
  );
};

export default InsulationDetail;
